import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ref, onValue, Unsubscribe } from 'firebase/database';
import { FirebaseConfigService } from '../firebase-config.service';
import { SignalProvider } from './signal-provider.interface';
import { SignalChange, SignalMap } from './signal.types';
import { environment } from 'src/environments/environment';

/**
 * Firebase Realtime Database implementation of SignalProvider.
 * Listens on `signals/{env}/family_{rootId}/{scope}` and emits only the keys that
 * changed since the last snapshot.
 *
 * NOTE: requires environment.firebase.databaseURL to be set, and a provisioned
 * RTDB instance — see Phase 0 spike (DEFERRED). Not registered/used until then.
 */
@Injectable({ providedIn: 'root' })
export class FirebaseSignalProvider extends SignalProvider {
  private changesSubject = new Subject<SignalChange>();
  private listeners = new Map<string, Unsubscribe>();
  private lastKnown = new Map<string, SignalMap>();
  /**
   * Scopes whose baseline snapshot has been taken. Tracked separately from
   * lastKnown because "no baseline yet" and "baseline was empty" are different
   * states — see the emit guard below.
   */
  private seeded = new Set<string>();

  /**
   * Shared value streams from watchValues(), keyed `${familyRootId}:${scope}`.
   * Cached so N subscribers share one RTDB listener rather than opening N.
   */
  private valueStreams = new Map<string, Observable<Record<string, any>>>();

  /**
   * Environment segment of every signal path.
   *
   * dev, UAT and production share one Firebase project and one RTDB instance, so without this
   * they read and write the same nodes — a drawing finishing on dev raised a notification on
   * production, and every widget-refresh and permission-sync signal crossed over too.
   *
   * Must match the backend's services.firebase.signal_namespace on the server this build talks
   * to; a mismatch means no signal ever arrives.
   */
  private ns: string = (environment as any).firebaseSignalNamespace || 'local';

  /**
   * Adopt the server's namespace. Anything already connected is torn down first: those
   * listeners are bound to the old path and would otherwise keep delivering from it.
   */
  setNamespace(namespace: string | null | undefined): void {
    const next = (namespace || '').trim() || (environment as any).firebaseSignalNamespace || 'local';

    if (next === this.ns) {
      return;
    }

    this.disconnectAll();
    this.ns = next;
  }

  constructor(private firebaseConfig: FirebaseConfigService, private zone: NgZone) {
    super();
  }

  get changes$(): Observable<SignalChange> {
    return this.changesSubject.asObservable();
  }

  connect(familyRootId: number, scope: string): void {
    const key = `${familyRootId}:${scope}`;
    if (this.listeners.has(key)) {
      return; // Already connected.
    }

    const db = this.firebaseConfig.getRealtimeDatabase();
    const signalRef = ref(db, `signals/${this.ns}/family_${familyRootId}/${scope}`);

    // Firebase RTDB callbacks arrive outside Angular's zone (the SDK's websocket transport is
    // not zone-patched), so subscribers see the data but Angular schedules no change detection.
    // Re-entering here fixes it once for every consumer rather than each one remembering to.
    const unsubscribe = onValue(signalRef, (snapshot) => this.zone.run(() => {
      const current: SignalMap = snapshot.val() || {};
      const previous = this.lastKnown.get(key) || {};

      // Diff: emit only keys whose timestamp actually changed.
      const changed: SignalMap = {};
      let hasChanges = false;
      for (const [signalKey, timestamp] of Object.entries(current)) {
        if (timestamp !== previous[signalKey]) {
          changed[signalKey] = timestamp as number;
          hasChanges = true;
        }
      }

      this.lastKnown.set(key, { ...current });

      // Skip the very first snapshot — it is the baseline, not a change.
      //
      // This used to test `Object.keys(previous).length > 0`, which conflated
      // "no baseline yet" with "baseline was empty" and swallowed the first real
      // signal whenever the node happened to be empty at connect time. That is
      // the normal state of the permissions node, which prunes itself empty
      // after an hour without a role change.
      if (!this.seeded.has(key)) {
        this.seeded.add(key);
        return;
      }

      if (hasChanges) {
        this.changesSubject.next({ scope, changed });
      }
    }));

    this.listeners.set(key, unsubscribe);
  }

  watchValues<T = any>(familyRootId: number, scope: string): Observable<Record<string, T>> {
    const key = `${familyRootId}:${scope}`;
    const existing = this.valueStreams.get(key);

    if (existing) {
      return existing as Observable<Record<string, T>>;
    }

    const stream = new Observable<Record<string, T>>((subscriber) => {
      const db = this.firebaseConfig.getRealtimeDatabase();
      const valueRef = ref(db, `signals/${this.ns}/family_${familyRootId}/${scope}`);

      const unsubscribe = onValue(
        valueRef,
        // Same zone re-entry as above: without it a payload arrives but no view updates.
        (snapshot) => this.zone.run(() => subscriber.next((snapshot.val() || {}) as Record<string, T>)),
        (error) => this.zone.run(() => subscriber.error(error))
      );

      // Teardown runs when the last subscriber unsubscribes (refCount below).
      return () => {
        unsubscribe();
        this.valueStreams.delete(key);
      };
    }).pipe(
      // Late subscribers get the latest snapshot immediately instead of waiting for the
      // next write, which for a finished drawing would never come.
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.valueStreams.set(key, stream);

    return stream;
  }

  disconnect(scope: string): void {
    for (const [key, unsub] of this.listeners.entries()) {
      if (key.endsWith(`:${scope}`)) {
        unsub();
        this.listeners.delete(key);
        this.lastKnown.delete(key);
        this.seeded.delete(key);
      }
    }
  }

  disconnectAll(): void {
    this.listeners.forEach((unsub) => unsub());
    this.listeners.clear();
    this.lastKnown.clear();
    this.seeded.clear();
    // Value streams tear themselves down via refCount when their subscribers unsubscribe;
    // dropping the cache here stops a stale office's stream being handed out after a switch.
    this.valueStreams.clear();
  }
}

